<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
  >
    <!-- Loading spinner -->
    <span v-if="loading" class="mr-2">
      <i class="fa-solid fa-spinner fa-spin"></i>
    </span>

    <!-- Icon (left) -->
    <span v-if="$slots['icon-left'] && !loading" class="mr-2">
      <slot name="icon-left"></slot>
    </span>

    <!-- Default slot (button text) -->
    <slot></slot>

    <!-- Icon (right) -->
    <span v-if="$slots['icon-right']" class="ml-2">
      <slot name="icon-right"></slot>
    </span>
  </button>
</template>

<script>
export default {
  name: 'BaseButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => [
        'primary',
        'secondary',
        'danger',
        'success',
        'warning',
        'info',
        'outline-primary',
        'outline-secondary',
        'outline-danger',
        'ghost',
        'icon'
      ].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value)
    },
    type: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'submit', 'reset'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: String,
      default: 'md',
      validator: (value) => ['none', 'sm', 'md', 'lg', 'xl', 'full'].includes(value)
    }
  },
  computed: {
    buttonClasses() {
      return [
        'inline-flex items-center justify-center font-medium text-center transition-colors duration-200 focus:outline-none',
        this.sizeClasses,
        this.variantClasses,
        this.roundedClasses,
        this.block ? 'w-full' : '',
        (this.disabled || this.loading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      ]
    },
    sizeClasses() {
      if (this.variant === 'icon') {
        const iconSizes = {
          xs: 'p-1',
          sm: 'p-1.5',
          md: 'p-2',
          lg: 'p-2.5',
          xl: 'p-3'
        }
        return iconSizes[this.size]
      }

      const sizes = {
        xs: 'px-2 py-1 text-xs',
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-sm',
        lg: 'px-5 py-2.5 text-base',
        xl: 'px-6 py-3 text-lg'
      }
      return sizes[this.size]
    },
    variantClasses() {
      const variants = {
        primary: 'bg-primary text-white border border-transparent hover:bg-primary-dark active:bg-primary-dark',
        secondary: 'bg-secondary text-white border border-transparent hover:bg-secondary-dark active:bg-secondary-dark',
        danger: 'bg-danger text-white border border-transparent hover:bg-red-600 active:bg-red-700',
        success: 'bg-success text-white border border-transparent hover:bg-green-600 active:bg-green-700',
        warning: 'bg-warning text-gray-900 border border-transparent hover:bg-yellow-500 active:bg-yellow-600',
        info: 'bg-info text-white border border-transparent hover:bg-blue-600 active:bg-blue-700',
        'outline-primary': 'bg-transparent text-primary border border-gray-300 hover:bg-primary hover:text-white hover:border-primary',
        'outline-secondary': 'bg-transparent text-secondary border border-gray-300 hover:bg-secondary hover:text-white hover:border-secondary',
        'outline-danger': 'bg-transparent text-danger border border-gray-300 hover:bg-danger hover:text-white hover:border-danger',
        ghost: 'bg-transparent text-gray-600 border border-transparent hover:bg-gray-100 active:bg-gray-200',
        icon: 'bg-transparent text-gray-500 border border-transparent hover:bg-gray-100 active:bg-gray-200 rounded-lg'
      }
      return variants[this.variant]
    },
    roundedClasses() {
      if (this.variant === 'icon') return 'rounded-lg'

      const rounded = {
        none: 'rounded-none',
        sm: 'rounded',
        md: 'rounded-lg',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        full: 'rounded-full'
      }
      return rounded[this.rounded]
    }
  }
}
</script>
